import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiErrorMessage } from "../lib/api";
import { Activity, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO = "https://images.unsplash.com/photo-1758206523830-a5b8afb372a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxDVCUyMHNjYW4lMjByYWRpb2xvZ2lzdCUyMG1lZGljYWwlMjBob3NwaXRhbCUyMHNoaWZ0JTIwc2NoZWR1bGV8ZW58MHx8fHwxNzkwMDI3NTYzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="CT scanner suite" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/85 via-slate-900/60 to-sky-900/40" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
              <Activity className="h-6 w-6" />
            </div>
            <span className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>RadiantShift</span>
          </div>
          <div>
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              CT Department<br />Scheduling, done right.
            </h1>
            <p className="mt-4 text-slate-200 max-w-md text-base leading-relaxed">
              Master schedules, open shifts, time-off and swaps — coordinated across 1st, 2nd and 3rd shift.
            </p>
          </div>
          <div className="text-xs text-slate-300 font-mono uppercase tracking-widest">Radiology Workforce Suite</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center text-white">
              <Activity className="h-5 w-5" />
            </div>
            <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>RadiantShift</span>
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Welcome back</h2>
          <p className="mt-2 text-sm text-slate-500">Sign in to access your schedule.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.org" required data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password-input" className="h-11" />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-xs text-slate-400">
            Staff accounts are created by your department manager. Contact them if you can't sign in.
          </p>
        </div>
      </div>
    </div>
  );
}
